// ============================================================================
// Fishing System Types - Fish types, rod tiers, fishing spot configs
// ============================================================================
import { ItemType } from '../items/types.js';
import { ObjectType } from '../objects/types.js';
import { TileType } from '../tiles/types.js';
// All fish types with their requirements
export const FISH_TYPES = {
    [ItemType.RAW_MINNOW]: {
        itemType: ItemType.RAW_MINNOW,
        cookedItemType: ItemType.COOKED_MINNOW,
        minFishingLevel: 0,
        baseCatchTimeMs: 3000,
        baseXpGain: 15,
        validWaterTypes: new Set([
            TileType.SHALLOW_WATER,
            TileType.WATER,
            TileType.DEEP_WATER,
            TileType.RIVER,
            TileType.SWAMP_WATER,
        ]),
    },
    [ItemType.RAW_SHRIMP]: {
        itemType: ItemType.RAW_SHRIMP,
        cookedItemType: ItemType.COOKED_SHRIMP,
        minFishingLevel: 12,
        baseCatchTimeMs: 6500,
        baseXpGain: 50,
        validWaterTypes: new Set([TileType.SHALLOW_WATER, TileType.WATER, TileType.DEEP_WATER]),
    },
    [ItemType.RAW_TROUT]: {
        itemType: ItemType.RAW_TROUT,
        cookedItemType: ItemType.COOKED_TROUT,
        minFishingLevel: 5,
        baseCatchTimeMs: 5000,
        baseXpGain: 30,
        validWaterTypes: new Set([TileType.RIVER]),
    },
    [ItemType.RAW_COD]: {
        itemType: ItemType.RAW_COD,
        cookedItemType: ItemType.COOKED_COD,
        minFishingLevel: 10,
        baseCatchTimeMs: 6000,
        baseXpGain: 45,
        validWaterTypes: new Set([TileType.WATER, TileType.DEEP_WATER]),
    },
    [ItemType.RAW_SALMON]: {
        itemType: ItemType.RAW_SALMON,
        cookedItemType: ItemType.COOKED_SALMON,
        minFishingLevel: 15,
        baseCatchTimeMs: 7000,
        baseXpGain: 60,
        validWaterTypes: new Set([TileType.RIVER]),
    },
    [ItemType.RAW_SWAMP_EEL]: {
        itemType: ItemType.RAW_SWAMP_EEL,
        cookedItemType: ItemType.COOKED_EEL,
        minFishingLevel: 20,
        baseCatchTimeMs: 8000,
        baseXpGain: 80,
        validWaterTypes: new Set([TileType.SWAMP_WATER]),
    },
};
export const FISHING_ROD_TIERS = {
    [ItemType.WOODEN_FISHING_ROD]: {
        itemType: ItemType.WOODEN_FISHING_ROD,
        speedMultiplier: 1.0,
        catchBonusPercent: 0,
        fishingLevelRequired: 0,
    },
    [ItemType.COPPER_FISHING_ROD]: {
        itemType: ItemType.COPPER_FISHING_ROD,
        speedMultiplier: 1.3,
        catchBonusPercent: 5,
        fishingLevelRequired: 10,
    },
    [ItemType.IRON_FISHING_ROD]: {
        itemType: ItemType.IRON_FISHING_ROD,
        speedMultiplier: 1.6,
        catchBonusPercent: 10,
        fishingLevelRequired: 20,
    },
    [ItemType.STEEL_FISHING_ROD]: {
        itemType: ItemType.STEEL_FISHING_ROD,
        speedMultiplier: 2.0,
        catchBonusPercent: 15,
        fishingLevelRequired: 35,
    },
};
// Sorted rod tiers by speed (ascending) for best-rod lookup
const SORTED_ROD_TIERS = Object.values(FISHING_ROD_TIERS).sort((a, b) => b.speedMultiplier - a.speedMultiplier);
export const FISHING_SPOT_CONFIGS = {
    [ObjectType.FISHING_SPOT_SHORE]: {
        objectType: ObjectType.FISHING_SPOT_SHORE,
        fishPool: [{ itemType: ItemType.RAW_MINNOW, weight: 1 }],
        minFishCount: 8,
        maxFishCount: 15,
        respawnDelayMs: 3 * 60 * 1000, // 3 minutes
        validWaterTypes: new Set([TileType.SHALLOW_WATER, TileType.WATER]),
    },
    [ObjectType.FISHING_SPOT_RIVER]: {
        objectType: ObjectType.FISHING_SPOT_RIVER,
        fishPool: [
            { itemType: ItemType.RAW_TROUT, weight: 3 },
            { itemType: ItemType.RAW_SALMON, weight: 1 },
        ],
        minFishCount: 6,
        maxFishCount: 12,
        respawnDelayMs: 4 * 60 * 1000, // 4 minutes
        validWaterTypes: new Set([TileType.RIVER]),
    },
    [ObjectType.FISHING_SPOT_OCEAN]: {
        objectType: ObjectType.FISHING_SPOT_OCEAN,
        fishPool: [
            { itemType: ItemType.RAW_COD, weight: 3 },
            { itemType: ItemType.RAW_SHRIMP, weight: 2 },
        ],
        minFishCount: 5,
        maxFishCount: 10,
        respawnDelayMs: 5 * 60 * 1000, // 5 minutes
        validWaterTypes: new Set([TileType.WATER, TileType.DEEP_WATER]),
    },
    [ObjectType.FISHING_SPOT_SWAMP]: {
        objectType: ObjectType.FISHING_SPOT_SWAMP,
        fishPool: [{ itemType: ItemType.RAW_SWAMP_EEL, weight: 1 }],
        minFishCount: 4,
        maxFishCount: 8,
        respawnDelayMs: 6 * 60 * 1000, // 6 minutes
        validWaterTypes: new Set([TileType.SWAMP_WATER]),
    },
};
// Set of all fishing spot object types
export const FISHING_SPOT_TYPES = new Set([
    ObjectType.FISHING_SPOT_SHORE,
    ObjectType.FISHING_SPOT_RIVER,
    ObjectType.FISHING_SPOT_OCEAN,
    ObjectType.FISHING_SPOT_SWAMP,
]);
// ============================================================================
// Helper Functions
// ============================================================================
export const getFishType = ({ itemType }) => {
    return FISH_TYPES[itemType] ?? null;
};
export const getFishingRodTier = ({ itemType, }) => {
    return FISHING_ROD_TIERS[itemType] ?? null;
};
export const isFishingRod = ({ itemType }) => {
    return FISHING_ROD_TIERS[itemType] !== undefined;
};
export const isFishingSpot = ({ objectType }) => {
    return FISHING_SPOT_TYPES.has(objectType);
};
export const getFishingSpotConfig = ({ objectType, }) => {
    return FISHING_SPOT_CONFIGS[objectType] ?? null;
};
// Get the best fishing rod from a list of item types (highest speed multiplier)
export const getBestFishingRod = ({ inventoryItemTypes, }) => {
    const inventorySet = new Set(inventoryItemTypes);
    for (const tier of SORTED_ROD_TIERS) {
        if (inventorySet.has(tier.itemType)) {
            return tier;
        }
    }
    return null;
};
// Get the fish pool available at a water edge (no fishing spot)
export const getWaterEdgeFishPool = ({ waterTileType, }) => {
    const pool = [];
    // Minnow available at any water edge
    pool.push({ itemType: ItemType.RAW_MINNOW, weight: 3 });
    if (waterTileType === TileType.SHALLOW_WATER ||
        waterTileType === TileType.WATER ||
        waterTileType === TileType.DEEP_WATER) {
        pool.push({ itemType: ItemType.RAW_SHRIMP, weight: 1 });
    }
    // Trout available at river edges
    if (waterTileType === TileType.RIVER) {
        pool.push({ itemType: ItemType.RAW_TROUT, weight: 1 });
    }
    return pool;
};
//# sourceMappingURL=types.js.map